import type { LoginInput, ModuleKey, Permission } from '@alicerce/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiClient } from './api';
import { prefs } from './storage';
import type { EntitlementStatus, Me } from './types';

interface SessionValue {
  api: ApiClient;
  status: 'loading' | 'anonymous' | 'authenticated';
  me: Me | null;
  tenantId: string | null;
  impersonating: boolean;
  login(input: LoginInput): Promise<void>;
  /** Guarda o token devolvido pelo cadastro. */
  startSession(accessToken: string): Promise<void>;
  logout(): Promise<void>;
  selectTenant(tenantId: string | null, opts?: { impersonate?: boolean }): void;
  refreshMe(): Promise<unknown>;
}

const SessionContext = createContext<SessionValue | null>(null);

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/**
 * Sessão do painel. O /me é lido em dois passos: sem negócio (usuário e lista de negócios) e, escolhido
 * o negócio, com X-Tenant-Id (papel, permissões e módulos). Um negócio salvo que não é mais do usuário
 * cai para o primeiro da lista, sem mandar um header que a API recusaria.
 */
export function SessionProvider({ children, client }: { children: ReactNode; client?: ApiClient }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(() => prefs.impersonating() ?? prefs.tenantId());
  const [impersonating, setImpersonating] = useState(() => !!prefs.impersonating());
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [api] = useState(() => client ?? new ApiClient({ baseUrl: API_URL }));

  useEffect(() => {
    api.setSessionExpiredHandler(() => {
      setStatus('anonymous');
      queryClient.clear();
    });
  }, [api, queryClient]);

  // Ao abrir o painel, tenta recuperar a sessão pelo cookie de refresh.
  useEffect(() => {
    let alive = true;
    void api.refresh().then((ok) => {
      if (alive) setStatus(ok ? 'authenticated' : 'anonymous');
    });
    return () => {
      alive = false;
    };
  }, [api]);

  const base = useQuery({
    queryKey: ['me', 'base'],
    queryFn: () => api.get<Me>('/v1/me', { noTenant: true }),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });

  const tenantId = useMemo(() => {
    if (impersonating) return chosen;
    const tenants = base.data?.tenants;
    if (!tenants) return null;
    return tenants.some((t) => t.tenantId === chosen) ? chosen : (tenants[0]?.tenantId ?? null);
  }, [impersonating, chosen, base.data]);

  // Antes das telas buscarem dados (efeitos passivos), o cliente já manda o negócio certo.
  useLayoutEffect(() => {
    api.setTenant(tenantId, impersonating);
  }, [api, tenantId, impersonating]);

  const current = useQuery({
    queryKey: ['me', 'tenant', tenantId, impersonating],
    queryFn: () => api.get<Me>('/v1/me', { tenantId: tenantId! }),
    enabled: status === 'authenticated' && !!tenantId,
    staleTime: 60_000,
  });

  const me: Me | null = base.data ? { ...base.data, current: current.data?.current ?? null } : null;

  const startSession = useCallback(
    async (accessToken: string) => {
      api.setAccessToken(accessToken);
      setStatus('authenticated');
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [api, queryClient],
  );

  const login = useCallback(
    async (input: LoginInput) => {
      const res = await api.post<{ accessToken: string }>('/v1/auth/login', input, { noTenant: true });
      await startSession(res.accessToken);
    },
    [api, startSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/v1/auth/logout', undefined, { noTenant: true });
    } finally {
      api.setAccessToken(null);
      prefs.setImpersonating(null);
      setImpersonating(false);
      setStatus('anonymous');
      queryClient.clear();
    }
  }, [api, queryClient]);

  const selectTenant = useCallback(
    (id: string | null, opts: { impersonate?: boolean } = {}) => {
      const imp = !!opts.impersonate && !!id;
      prefs.setImpersonating(imp ? id : null);
      if (!imp && id) prefs.setTenantId(id);
      api.setTenant(id, imp);
      setImpersonating(imp);
      setChosen(imp ? id : (id ?? prefs.tenantId()));
      // Dados de outro negócio não podem aparecer nem por um instante.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
    [api, queryClient],
  );

  const refreshMe = useCallback(() => queryClient.invalidateQueries({ queryKey: ['me'] }), [queryClient]);

  const loading = status === 'loading' || (status === 'authenticated' && base.isPending);
  const value = useMemo<SessionValue>(
    () => ({
      api,
      status: loading ? 'loading' : status,
      me,
      tenantId,
      impersonating,
      login,
      startSession,
      logout,
      selectTenant,
      refreshMe,
    }),
    // `me` é recriado a cada render; as dependências reais são os dados das duas consultas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, loading, status, base.data, current.data, tenantId, impersonating, login, startSession, logout, selectTenant, refreshMe],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession fora do SessionProvider');
  return ctx;
}

export function useApi() {
  return useSession().api;
}

/** Papel, permissões e módulos do negócio atual. */
export function useCurrent() {
  const { me, impersonating } = useSession();
  const current = me?.current ?? null;
  const tenant = me?.tenants.find((t) => t.tenantId === current?.tenantId) ?? null;
  return useMemo(() => {
    const permissions = new Set<Permission>(current?.permissions ?? []);
    const moduleStatus = (key: ModuleKey): EntitlementStatus => {
      if (key === 'core' || key === 'billing') return 'ACTIVE';
      return current?.entitlements.find((e) => e.moduleKey === key)?.status ?? 'DISABLED';
    };
    return {
      current,
      tenant,
      impersonating,
      role: current?.role ?? null,
      // Suporte em "entrar como" só lê: nenhuma ação de escrita aparece.
      can: (p: Permission) => permissions.has(p) && (!impersonating || p.endsWith(':read')),
      moduleStatus,
      isModuleActive: (key: ModuleKey) => moduleStatus(key) !== 'DISABLED',
      readOnly: impersonating || tenant?.status === 'READ_ONLY',
    };
  }, [current, tenant, impersonating]);
}
